import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Grid,
  Paper,
  Space,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import {
  IconActivity,
  IconCalendar,
  IconChevronRight,
  IconClipboardList,
  IconFile,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { LibraryItem } from "@/components/LibraryItem";
import { NotificationItem } from "@/components/NotificationItem";
import { Portrait } from "@/components/Portrait";
import { SurveyList } from "@/components/SurveyList";
import { useHashTab } from "@/hooks/useHashTab";
import { useNotifications } from "@/hooks/useNotifications";
import { TrainerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";
import type { SurveyStatus } from "@/utils/survey";
import { SessionListContent } from "./SessonList";

export const ClientsList = () => {
  const { navigator } = useNavigator();
  const { data: engagements, isLoading } = client.useQuery(
    "get",
    "/trainer/engagements/",
  );

  if (isLoading || !engagements) {
    return <FancyLoader />;
  }

  return (
    <Stack>
      <Header title="Clients" />
      {engagements.engagements.map((engagement) => (
        <Paper
          key={engagement.id}
          p="md"
          withBorder
          bg="blue.0"
          onClick={navigator({
            path: TrainerRoutes.CLIENTS.DETAIL,
            params: { engagementId: engagement.id },
          })}
        >
          <Flex gap="sm" align="center">
            <Portrait profile={engagement.owner} size="lg" />
            <Box style={{ flex: "1 1 auto" }}>
              <Text fw="bold">
                {engagement.owner.firstName} {engagement.owner.lastName}
              </Text>
            </Box>
            <Box>
              <ActionIcon variant="transparent" size="lg" color="gray">
                <IconChevronRight />
              </ActionIcon>
            </Box>
          </Flex>
        </Paper>
      ))}
    </Stack>
  );
};
export const ClientDetail = () => {
  const { navigator, navigate } = useNavigator();
  const { engagementId } = useParams<{ engagementId: string }>();
  type TabType = "info" | "sessions" | "files" | "surveys" | "activity";
  const { activeTab, onTabChange } = useHashTab<TabType>("info");
  const { notifications, deleteNotifications } = useNotifications();

  const {
    data: engagement,
    isLoading,
    refetch: refetchEngagement,
  } = client.useQuery("get", "/trainer/engagements/{engagementId}", {
    params: { path: { engagementId } },
  });

  const removeFileFromEngagement = client.useMutation(
    "post",
    "/trainer/engagements/{engagementId}/remove-file",
  );

  const handleRemoveFile = async (fileId: string) => {
    try {
      await removeFileFromEngagement.mutateAsync({
        params: {
          path: { engagementId },
        },
        body: {
          fileId: fileId,
        },
      });
      await refetchEngagement();
    } catch (error) {
      console.error("Error removing file from engagement:", error);
    }
  };

  const handleTabChange = (value: string | null) => {
    if (value && (value as TabType)) {
      onTabChange(value as TabType);
    }
  };

  const handleSurveyClick = (surveyId: string, _status: SurveyStatus) => {
    // Navigate to survey results page
    navigate({
      path: TrainerRoutes.SURVEYS.RESULTS,
      params: {
        engagementId,
        surveyId: surveyId,
      },
    });
  };

  if (isLoading || !engagement) {
    return <FancyLoader />;
  }

  const engagementNotifications = notifications.filter(
    (notification) => notification.engagementId === engagementId,
  );

  return (
    <Stack>
      <Header title="Client Details" />
      <Stack>
        <Flex gap="md" align="center">
          <Portrait profile={engagement.owner} size="xl" />
          <Box>
            <Title order={3}>
              {engagement.owner.firstName} {engagement.owner.lastName}
            </Title>
          </Box>
        </Flex>

        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List grow justify="center">
            <Tabs.Tab value="info" leftSection={<IconInfoCircle size={16} />}>
              Info
            </Tabs.Tab>
            <Tabs.Tab value="sessions" leftSection={<IconCalendar size={16} />}>
              Sessions
            </Tabs.Tab>
            <Tabs.Tab value="files" leftSection={<IconFile size={16} />}>
              Files
            </Tabs.Tab>
            <Tabs.Tab
              value="surveys"
              leftSection={<IconClipboardList size={16} />}
            >
              Surveys
            </Tabs.Tab>
            <Tabs.Tab value="activity" leftSection={<IconActivity size={16} />}>
              Activity
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info">
            <Box mt="md">
              <Title order={4}>Dogs</Title>
              <Space h="md" />
              {engagement.dogs.map((dog, index) => (
                <Paper key={index} p="md" withBorder>
                  <Grid>
                    <Grid.Col span="auto">
                      <Flex gap="md" align="center">
                        <Portrait profile={dog} size="lg" who="dog" />
                        <Text fw="bold">{dog.name}</Text>
                      </Flex>
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Stack gap="xs">
                        <Text size="sm">
                          {dog.breed} • {dog.age} years old
                        </Text>
                        <Text size="sm">Energy level: {dog.energyLevel}</Text>
                        <Text size="sm">Temperament: {dog.temperament}</Text>
                        <Text size="sm">
                          Training needs: {dog.trainingNeeds ?? "not specified"}
                        </Text>
                        <Text size="sm">
                          Health concerns:{" "}
                          {dog.healthConcerns ?? "not specified"}
                        </Text>
                      </Stack>
                    </Grid.Col>
                  </Grid>
                </Paper>
              ))}
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="sessions">
            <Box mt="md">
              <SessionListContent
                sessionList={engagement.sessions}
                onlyListView={true}
              />
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="files">
            <Box mt="md">
              {engagement.files.length === 0 ? (
                <Paper p="md">
                  <Text ta="center" c="dimmed">
                    No files shared with client
                  </Text>
                </Paper>
              ) : (
                <Stack gap="md">
                  {engagement.files
                    .filter((file) => file.type !== "SURVEY")
                    .map((file) => (
                      <LibraryItem
                        key={file.id}
                        item={file}
                        kind="file"
                        readonly={true}
                        onDelete={handleRemoveFile}
                        onFileClick={navigator({
                          path: UniversalRoutes.FILE_PREVIEW,
                          params: { fileId: file.id },
                        })}
                      />
                    ))}
                </Stack>
              )}
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="surveys">
            <Box mt="md">
              <SurveyList
                files={engagement.files}
                surveyStatuses={engagement.surveys}
                onSurveyClick={handleSurveyClick}
              />
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="activity">
            <Stack gap="xs">
              <Space h="xs" />
              {engagementNotifications.length === 0 ? (
                <Paper p="md">
                  <Text ta="center" c="dimmed">
                    No recent activity
                  </Text>
                </Paper>
              ) : (
                <Stack gap="md">
                  <Button
                    variant="outline"
                    onClick={() =>
                      deleteNotifications(
                        engagementNotifications.map(
                          (notification) => notification.id,
                        ),
                      )
                    }
                  >
                    Clear all
                  </Button>
                  {engagementNotifications
                    .filter(
                      (notification) => notification.type !== "CHAT_MESSAGE",
                    )
                    .map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        role="trainer"
                        notification={notification}
                        onDelete={deleteNotifications}
                      />
                    ))}
                </Stack>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Stack>
  );
};
